import { z } from "zod";
import { submissionFilterSchema } from "./submissionQuery.js";

/**
 * The analytics contract — docs/01-PRODUCT-SPEC.md §10, docs/03-API-SPEC.md §8.
 *
 * Two halves that never meet: what the public renderer *sends* (events, in `analyticsBatchSchema`)
 * and what the dashboard *asks* (queries, everything below it). They live in one file because both
 * are the analytics vocabulary, and putting the event names anywhere else would let the renderer
 * and the rollup drift apart with nothing to catch it.
 */

/**
 * The five events from the phase prompt, and nothing else.
 *
 * A closed enum rather than a free string is what enforces "no personal data in events": there is
 * no event shape in which an answer, an email address or a name can travel. The renderer cannot
 * invent `field_value` even by accident.
 */
export const ANALYTICS_EVENT_TYPES = [
  "form_view",
  "form_start",
  "page_complete",
  "field_abandon",
  "form_submit",
] as const;

export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

/** Device buckets. Derived server-side from the User-Agent; never sent by the client. */
export const ANALYTICS_DEVICES = ["mobile", "tablet", "desktop", "unknown"] as const;
export type AnalyticsDevice = (typeof ANALYTICS_DEVICES)[number];

/**
 * Field keys are `snake_case` and validated here for the same reason `submissionQuery` validates
 * them: they end up as a column value in a rollup row that later reaches SQL, and an unbounded
 * string from an anonymous public endpoint is not something to hand a query builder.
 */
const eventFieldKey = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);

export const analyticsEventSchema = z.object({
  type: z.enum(ANALYTICS_EVENT_TYPES),
  /** 1-based. Present on `page_complete`; ignored elsewhere. */
  page: z.number().int().min(1).max(200).optional(),
  /** Present on `field_abandon` — the last field focused before the applicant left. */
  fieldKey: eventFieldKey.optional(),
});

export type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;

/**
 * Events arrive batched. The renderer buffers them and flushes on `visibilitychange`, which is the
 * only moment a `field_abandon` can honestly be known — and the only moment `sendBeacon` is
 * guaranteed to still run.
 */
export const analyticsBatchSchema = z.object({
  events: z.array(analyticsEventSchema).min(1).max(50),
  /** `utm_source` as the applicant arrived with it. Truncated hard; it is a label, not a payload. */
  utmSource: z.string().max(64).optional(),
});

export type AnalyticsBatch = z.infer<typeof analyticsBatchSchema>;

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * `filters` arrives as a JSON string in the query string, exactly as the submissions list takes it
 * (docs/03-API-SPEC.md §6/§8: "All analytics endpoints accept the same filter object as the
 * submissions list"). Reusing `submissionFilterSchema` is the point — a coordinator who filters the
 * table and then opens analytics must be looking at the same population.
 */
const jsonParam = <T extends z.ZodTypeAny>(inner: T) =>
  z
    .string()
    .transform((value, ctx): unknown => {
      try {
        return JSON.parse(value);
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "not valid JSON" });
        return z.NEVER;
      }
    })
    .pipe(inner);

const booleanParam = z
  .enum(["true", "false"])
  .default("false")
  .transform((value) => value === "true");

/** Every analytics endpoint takes these. `fresh=true` bypasses the five-minute Redis cache. */
export const analyticsBaseQuerySchema = z.object({
  filters: jsonParam(z.array(submissionFilterSchema).max(25)).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  fresh: booleanParam,
});

export const analyticsOverviewQuerySchema = analyticsBaseQuerySchema.extend({
  bucket: z.enum(["day", "week"]).default("day"),
});

export type AnalyticsOverviewQuery = z.output<typeof analyticsOverviewQuerySchema>;

export const analyticsFieldQuerySchema = analyticsBaseQuerySchema.extend({
  /** A second field key to break this question down by. `data.` prefix omitted — it is always a field. */
  crossTabBy: eventFieldKey.optional(),
  /** Caps a cross-tab matrix; a free-text field crossed by another can otherwise be enormous. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export type AnalyticsFieldQuery = z.output<typeof analyticsFieldQuerySchema>;

export const programmeAnalyticsQuerySchema = analyticsBaseQuerySchema.extend({
  /** Comma-separated cycle ids. Absent means every cycle in the programme. */
  cycleIds: z
    .string()
    .max(1000)
    .optional()
    .transform((value) =>
      value
        ? value
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        : undefined,
    ),
});

export type ProgrammeAnalyticsQuery = z.output<typeof programmeAnalyticsQuerySchema>;

export const analyticsDigestSettingSchema = z.object({
  enabled: z.boolean(),
  dayOfWeek: z.number().int().min(1).max(7),
  hour: z.number().int().min(0).max(23),
  recipients: z.array(z.string().max(120)).max(50).default([]),
  extraEmails: z.array(z.string().email()).max(50).default([]),
});

export type AnalyticsDigestSetting = z.infer<typeof analyticsDigestSettingSchema>;

// ─── Response shapes ─────────────────────────────────────────────────────────

/** Every analytics response is wrapped in this. `cached` is what makes a stale figure explicable. */
export interface AnalyticsEnvelope<T> {
  generatedAt: string;
  cached: boolean;
  data: T;
}

/**
 * The distribution shapes, one per field family. A discriminated union rather than a bag of
 * optional keys: the dashboard picks its chart from `kind`, so a field type with no matching chart
 * is a type error at build time rather than an empty panel at run time.
 */
export type FieldDistribution =
  | { kind: "categorical"; total: number; buckets: { value: string; count: number }[] }
  | {
      kind: "numeric";
      total: number;
      mean: number | null;
      median: number | null;
      mode: number | null;
      min: number | null;
      max: number | null;
      histogram: { from: number; to: number; count: number }[];
    }
  | {
      kind: "rating";
      total: number;
      mean: number | null;
      buckets: { value: string; count: number }[];
    }
  | {
      kind: "nps";
      total: number;
      score: number | null;
      promoters: number;
      passives: number;
      detractors: number;
      buckets: { value: string; count: number }[];
    }
  | {
      kind: "matrix";
      rows: string[];
      columns: string[];
      cells: { row: string; column: string; count: number }[];
    }
  | { kind: "text"; total: number; terms: { term: string; count: number }[] };

export interface CrossTab {
  rows: string[];
  columns: string[];
  cells: { row: string; column: string; count: number }[];
}

/**
 * Stop words removed from free-text term frequency.
 *
 * English function words plus the phrases every applicant essay in this programme repeats —
 * "scholarship", "seplat", "apply". Without those, the top-50 for every essay field is the same
 * fifty words and tells a coordinator nothing. Kept here rather than in Postgres's own stopword
 * dictionary because that would need a file installed in the database image, which the deployment
 * rules do not allow us to add to a pinned Postgres base.
 */
export const TEXT_STOP_WORDS: readonly string[] = [
  "a",
  "about",
  "above",
  "after",
  "again",
  "against",
  "all",
  "am",
  "an",
  "and",
  "any",
  "are",
  "as",
  "at",
  "be",
  "because",
  "been",
  "before",
  "being",
  "below",
  "between",
  "both",
  "but",
  "by",
  "can",
  "cannot",
  "could",
  "did",
  "do",
  "does",
  "doing",
  "down",
  "during",
  "each",
  "few",
  "for",
  "from",
  "further",
  "had",
  "has",
  "have",
  "having",
  "he",
  "her",
  "here",
  "hers",
  "herself",
  "him",
  "himself",
  "his",
  "how",
  "i",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "itself",
  "just",
  "me",
  "more",
  "most",
  "my",
  "myself",
  "no",
  "nor",
  "not",
  "now",
  "of",
  "off",
  "on",
  "once",
  "only",
  "or",
  "other",
  "our",
  "ours",
  "ourselves",
  "out",
  "over",
  "own",
  "same",
  "she",
  "should",
  "so",
  "some",
  "such",
  "than",
  "that",
  "the",
  "their",
  "theirs",
  "them",
  "themselves",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "through",
  "to",
  "too",
  "under",
  "until",
  "up",
  "very",
  "was",
  "we",
  "were",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "whom",
  "why",
  "will",
  "with",
  "would",
  "you",
  "your",
  "yours",
  "yourself",
  "yourselves",
  // Programme-specific noise.
  "apply",
  "applying",
  "application",
  "scholarship",
  "seplat",
  "programme",
  "program",
  "academy",
  "want",
  "need",
  "study",
  "studying",
  "school",
  "because",
  "also",
];
